/**
 * Replay Recorder Service
 * Records game events with client-side buffering to handle interruptions
 * Periodically flushes to server and handles page unload
 *
 * Now with Zod validation to catch errors before sending to server!
 */

import { EventType } from '../types/replay';
import {
  CreateSessionRequestSchema,
  UpdateSessionRequestSchema,
  ReplayEventSchema,
  validateData
} from '../shared-types/replay';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
const FLUSH_INTERVAL_MS = 5000; // Flush every 5 seconds
const MAX_BUFFER_SIZE = 50; // Flush after 50 events
const MAX_CHUNK_SIZE = 100; // Maximum events per request to avoid payload size limits
const MAX_OFFLINE_BUFFER = 5000; // Max events to keep if server unreachable (prevents memory issues)
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff for retries

class ReplayRecorder {
  constructor(sessionId, sessionData) {
    this.sessionId = sessionId;
    this.sessionData = sessionData;
    this.eventBuffer = [];
    this.nextSequence = 0;
    this.flushTimer = null;
    this.isFlushing = false;
    this.isSessionCreated = false;

    // Bind methods
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);

    // Register page unload handler
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // NOTE: Session is NOT created here - must call createSession() explicitly
    // before recording events

    // Start periodic flush
    this.startPeriodicFlush();
  }

  /**
   * Create session on server
   */
  async createSession() {
    try {
      // Validate session data before sending
      const validation = validateData(CreateSessionRequestSchema, this.sessionData);

      if (!validation.success) {
        console.error('Invalid session data:', validation.errors.errors);
        console.error('Session data:', this.sessionData);
        throw new Error(`Invalid session data: ${validation.errors.errors.map(e => e.message).join(', ')}`);
      }

      const response = await fetch(`${SERVER_URL}/api/alien2048/replay/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validation.data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server rejected session creation:', errorData);
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      this.isSessionCreated = true;
      console.log(`Replay session created: ${this.sessionId}`);
    } catch (error) {
      console.error('Error creating replay session:', error);
      // Continue recording locally even if server fails
    }
  }

  /**
   * Record an event
   * @param {string} eventType - Type of event (from EventType enum)
   * @param {number} timestampMs - Elapsed time in milliseconds
   * @param {Object} data - Event data
   */
  recordEvent(eventType, timestampMs, data) {
    const event = {
      event_type: eventType,
      timestamp_ms: timestampMs,
      data,
    };

    this.eventBuffer.push(event);
    this.nextSequence++;

    // Flush if buffer is full
    if (this.eventBuffer.length >= MAX_BUFFER_SIZE) {
      this.flush();
    }
  }

  /**
   * Start periodic flush timer
   */
  startPeriodicFlush() {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  /**
   * Stop periodic flush timer
   */
  stopPeriodicFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Flush buffered events to server
   */
  async flush() {
    // Don't flush if already flushing or buffer is empty
    if (this.isFlushing || this.eventBuffer.length === 0) {
      return;
    }

    // Wait for session to be created
    if (!this.isSessionCreated) {
      return;
    }

    this.isFlushing = true;
    const eventsToFlush = [...this.eventBuffer];
    this.eventBuffer = [];

    try {
      // Split into chunks if needed to avoid payload size limits
      if (eventsToFlush.length > MAX_CHUNK_SIZE) {
        console.log(`Splitting ${eventsToFlush.length} events into chunks of ${MAX_CHUNK_SIZE}`);
        for (let i = 0; i < eventsToFlush.length; i += MAX_CHUNK_SIZE) {
          const chunk = eventsToFlush.slice(i, i + MAX_CHUNK_SIZE);
          await this.sendEvents(chunk);
          console.log(`Flushed chunk ${Math.floor(i / MAX_CHUNK_SIZE) + 1}/${Math.ceil(eventsToFlush.length / MAX_CHUNK_SIZE)} (${chunk.length} events)`);
        }
      } else {
        await this.sendEvents(eventsToFlush);
        console.log(`Flushed ${eventsToFlush.length} events to server`);
      }
    } catch (error) {
      console.error('Error flushing events:', error);
      // Put events back in buffer to retry later, but cap at MAX_OFFLINE_BUFFER
      this.eventBuffer = [...eventsToFlush, ...this.eventBuffer];
      if (this.eventBuffer.length > MAX_OFFLINE_BUFFER) {
        console.warn(`Buffer exceeded ${MAX_OFFLINE_BUFFER} events, dropping oldest`);
        this.eventBuffer = this.eventBuffer.slice(-MAX_OFFLINE_BUFFER);
      }
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Send events to server with retry logic
   * @param {Array} events - Array of events to send
   * @param {number} retryCount - Current retry attempt
   */
  async sendEvents(events, retryCount = 0) {
    try {
      const response = await fetch(
        `${SERVER_URL}/api/alien2048/replay/sessions/${this.sessionId}/events`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ events }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to send events: ${response.statusText}`);
      }
    } catch (error) {
      // Retry with exponential backoff
      if (retryCount < RETRY_DELAYS.length) {
        const delay = RETRY_DELAYS[retryCount];
        console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.sendEvents(events, retryCount + 1);
      }
      throw error;
    }
  }

  /**
   * Update session with final stats
   * @param {Object} updates - Fields to update (final_score, final_moves, etc.)
   * @returns {Promise<Object>} - Server response with score_id, validated_score, validation_errors
   */
  async updateSession(updates) {
    try {
      // Validate update data before sending
      const validation = validateData(UpdateSessionRequestSchema, updates);

      if (!validation.success) {
        console.error('Invalid update data:', validation.errors.errors);
        console.error('Update data:', updates);
        throw new Error(`Invalid update data: ${validation.errors.errors.map(e => e.message).join(', ')}`);
      }

      const response = await fetch(
        `${SERVER_URL}/api/alien2048/replay/sessions/${this.sessionId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(validation.data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server rejected session update:', errorData);
        throw new Error(`Failed to update session: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Session updated', responseData);
      return responseData;
    } catch (error) {
      console.error('Error updating session:', error);
      return { success: false, score_id: null, validated_score: null, validation_errors: [error.message] };
    }
  }

  /**
   * Handle page unload - synchronous flush with sendBeacon
   */
  handleBeforeUnload() {
    // Only send events if session was created (game was completed)
    if (this.isSessionCreated && this.eventBuffer.length > 0) {
      // Use sendBeacon for reliable delivery during page unload
      const url = `${SERVER_URL}/api/alien2048/replay/sessions/${this.sessionId}/events`;
      const data = JSON.stringify({ events: this.eventBuffer });

      // navigator.sendBeacon is limited to POST requests
      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        console.log('Sent remaining events via sendBeacon');
      }
    }
  }

  /**
   * Finalize recording - flush remaining events and update session
   * @param {Object} finalStats - Final game statistics (includes game_state and final_board)
   * @returns {Promise<Object>} - Server response with score_id, validated_score, validation_errors
   */
  async finalize(finalStats) {
    // Stop periodic flush
    this.stopPeriodicFlush();

    let result = { success: false, score_id: null, validated_score: null, validation_errors: null };

    // Only create session and save replay if game was completed (game_state: 2)
    if (finalStats.game_state === 2) {
      // Create session on server if not already created
      if (!this.isSessionCreated) {
        console.log('Game completed - creating replay session on server');
        await this.createSession();
      }

      // Record final BOARD_STATE for sanity check in tests
      if (finalStats.final_board) {
        const finalBoardChanges = [];
        for (let row = 0; row < finalStats.final_board.length; row++) {
          for (let col = 0; col < finalStats.final_board[row].length; col++) {
            finalBoardChanges.push([row, col, finalStats.final_board[row][col]]);
          }
        }
        this.recordBoardState(
          finalStats.duration_ms,
          finalBoardChanges,
          'final_state'
        );
        console.log(`Recorded final BOARD_STATE with ${finalBoardChanges.length} cells`);
      }

      // Flush remaining events (including final BOARD_STATE)
      await this.flush();

      // Update session with final stats (strip out final_board as it's not in the schema)
      // Keep highest_tile as it's needed for score validation
      const { final_board, ...sessionUpdates } = finalStats;
      result = await this.updateSession(sessionUpdates);

      console.log('Replay recording finalized and saved', result);
    } else {
      console.log('Game not completed - discarding replay data');
    }

    // Clean up event listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    return result;
  }

  /**
   * Convenience methods for recording common events
   */

  recordBoardInit(changes, boardSize) {
    this.recordEvent(EventType.BOARD_INIT, 0, {
      changes,
      board_size: boardSize,
    });
  }

  recordBoardState(timestampMs, changes, context) {
    this.recordEvent(EventType.BOARD_STATE, timestampMs, {
      changes,
      context,
    });
  }

  recordSwap(timestampMs, from, to, valid, fromValue = null, toValue = null) {
    const data = {
      from,
      to,
      valid,
    };

    // Add cell values if provided (enhanced recording)
    if (fromValue !== null) {
      data.from_value = fromValue;
    }
    if (toValue !== null) {
      data.to_value = toValue;
    }

    this.recordEvent(EventType.SWAP, timestampMs, data);
  }

  recordScoreUpdate(timestampMs, score, delta, reason) {
    this.recordEvent(EventType.SCORE_UPDATE, timestampMs, {
      score,
      delta,
      reason,
    });
  }

  recordPowerUp(timestampMs, position, piecesCleared) {
    this.recordEvent(EventType.POWER_UP, timestampMs, {
      position,
      pieces_cleared: piecesCleared,
    });
  }

  recordGameState(timestampMs, state, movesUsed, timeElapsedMs) {
    this.recordEvent(EventType.GAME_STATE, timestampMs, {
      state,
      moves_used: movesUsed,
      time_elapsed_ms: timeElapsedMs,
    });
  }

  recordBarrelPlace(timestampMs, changes) {
    this.recordEvent(EventType.BARREL_PLACE, timestampMs, {
      changes,
    });
  }

  /**
   * NEW: Record cells being cleared
   * @param {number} timestampMs - Elapsed time in milliseconds
   * @param {Array} clearedCells - Array of [row, col, oldValue] for each cleared cell
   * @param {string} reason - Reason for clearing: 'match', 'power_up', or 'cascade'
   */
  recordClear(timestampMs, clearedCells, reason = 'match') {
    this.recordEvent(EventType.CLEAR, timestampMs, {
      cleared: clearedCells,
      reason,
    });
  }

  /**
   * NEW: Record gravity movements
   * @param {number} timestampMs - Elapsed time in milliseconds
   * @param {Array} movements - Array of {from: [r,c], to: [r2,c2], value: n}
   */
  recordGravity(timestampMs, movements) {
    this.recordEvent(EventType.GRAVITY, timestampMs, {
      movements,
    });
  }

  /**
   * NEW: Record new pieces being added
   * @param {number} timestampMs - Elapsed time in milliseconds
   * @param {Array} newPieces - Array of [row, col, newValue] for each filled cell
   */
  recordFill(timestampMs, newPieces) {
    this.recordEvent(EventType.FILL, timestampMs, {
      new_pieces: newPieces,
    });
  }
}

export default ReplayRecorder;
